/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getPageBitmap, getPageTiles, getTileBitmap } from "selectors/pageSelectors";
import { getCurrRHPFeature } from "selectors/appSelectors";
import { thumbnailsInView } from "selectors/pagedViewSelectors";
import { getThumbnailBitmap } from "selectors/thumbnailSelectors";
import { rhpDropinIds, docViewModes } from "../constants/enums";
import { getViewMode } from "selectors/viewModeSelectors";
import * as stores from "stores";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import { previewEvents } from "publicApis/Events/previewEvents";
import { eventNames } from "publicApis/constants";
import { calculatePagesInViewData } from "publicApis/Events/events";

let pagesDisposerArray = [];
let thumbnailsDisposerArray = [];
let pagesInViewDisposer;
let thumbnailsInViewDisposer;
let currRhpFeatureDisposer;

const compareOldAndNewVal = (oldVal, newVal) => {
  if ((oldVal && !newVal) || (!oldVal && newVal) || oldVal.start !== newVal.start || oldVal.end !== newVal.end)
    return true;
  return false;
};

const clearDisposerArray = disposerArray => {
  if (disposerArray && disposerArray.length) {
    disposerArray.forEach(disposer => disposer());
  }
  return [];
};

const subscribeToPageTilesOfEachPage = (tilesArray, i) => {
  /* Subscribe to getTileBitmap for each tile of each page */
  if (tilesArray && tilesArray.length) {
    for (let j = 0; j < tilesArray.length; j++) {
      pagesDisposerArray.push(
        subscribeToSelector(
          stores,
          getTileBitmap,
          (value, reaction) => {
            setShouldShowPageLoader();
          },
          (oldVal, newVal) => oldVal !== newVal,
          i,
          j
        )
      );
    }
  }
};

const updateDisposerArray = (value, disposerArray, selector, selectorCallback = setShouldShowPageLoader) => {
  let { start, end } = value;
  if (start !== -1) {
    for (let i = start; i <= end; i++) {
      disposerArray.push(
        subscribeToSelector(
          stores,
          selector,
          (selectorValue, reaction) => {
            if (selectorCallback === setShouldShowPageLoader) selectorCallback();
            else selectorCallback(selectorValue, i);
          },
          (oldVal, newVal) => oldVal !== newVal,
          i
        )
      );
    }
  }
};

const getPagesInViewFromCalculatedData = data => {
  if (!data) data = calculatePagesInViewData(stores);
  return {
    start: data.startPage.pageNumber - 1,
    end: data.endPage.pageNumber - 1
  };
};

const subscribeToPagesInView = data => {
  const pagesInView = getPagesInViewFromCalculatedData(data);
  /* Disposing off exisiting autoruns for pages that are no longer in view */
  pagesDisposerArray = clearDisposerArray(pagesDisposerArray);
  /* Update the pageDisposer Array with new disposers after subscribing new pages*/
  updateDisposerArray(pagesInView, pagesDisposerArray, getPageBitmap);
  /* Subscribe to  getPageTiles and also subscribe to getTileBitmap for each tile of all pagesInView */
  updateDisposerArray(pagesInView, pagesDisposerArray, getPageTiles, subscribeToPageTilesOfEachPage);
  /* Even if pages in view change, evaluate whether to show page loader */
  setShouldShowPageLoader();
};

const subscribeToThumbnailsInView = () => {
  currRhpFeatureDisposer = subscribeToSelector(stores, getCurrRHPFeature, (value, reaction) => {
    if (value === rhpDropinIds.THUMBNAILS) {
      thumbnailsInViewDisposer = subscribeToSelector(
        stores,
        thumbnailsInView,
        (value, reaction) => {
          /* Disposing off exisiting autoruns for tuhmbnails that are no longer in view */
          thumbnailsDisposerArray = clearDisposerArray(thumbnailsDisposerArray);
          /* Update the pageDisposer Array with new disposers after subscribing new pages*/
          updateDisposerArray(value, thumbnailsDisposerArray, getThumbnailBitmap);
          /* Even if thumbnails in view change, evaluate whether to show page loader */
          setShouldShowPageLoader();
        },
        compareOldAndNewVal
      );
    } else {
      thumbnailsDisposerArray = clearDisposerArray(thumbnailsDisposerArray);
      if (thumbnailsInViewDisposer) thumbnailsInViewDisposer();
      thumbnailsInViewDisposer = undefined;
    }
    setShouldShowPageLoader();
  });
};

export const subscribeToPageLoader = () => {
  previewEvents.on(eventNames.PAGES_IN_VIEW_CHANGE, subscribeToPagesInView);
  subscribeToThumbnailsInView();
};

export const unsubscribeFromPageLoader = () => {
  /* Dispose off disposer arrays */
  thumbnailsDisposerArray = clearDisposerArray(thumbnailsDisposerArray);
  pagesDisposerArray = clearDisposerArray(pagesDisposerArray);
  /* Dispose off pagesInView */
  if (pagesInViewDisposer) pagesInViewDisposer();
  /* Dispose off thumbnailsInView */
  if (thumbnailsInViewDisposer) thumbnailsInViewDisposer();
  /* Dispose off curr RHP feature */
  if (currRhpFeatureDisposer) currRhpFeatureDisposer();
  pagesInViewDisposer = undefined;
  thumbnailsInViewDisposer = undefined;
  currRhpFeatureDisposer = undefined;
};

const arePagesInViewLoaded = () => {
  let pages = getPagesInViewFromCalculatedData();
  if (pages.start === -1) return true;

  const pagesLoaded = [];
  for (let i = pages.start; i <= pages.end; i++) {
    const isPageBitmapLoaded = !!getPageBitmap(stores, i);
    const pageTiles = getPageTiles(stores, i) || [];
    const isTileBitmapLoaded = !isPageBitmapLoaded && pageTiles.some(tile => tile && !!tile.tileBitmap);
    pagesLoaded.push(isPageBitmapLoaded || isTileBitmapLoaded);
  }
  return pagesLoaded.every(Boolean);
};

const areThumbnailsInViewLoaded = () => {
  if (getCurrRHPFeature(stores) !== rhpDropinIds.THUMBNAILS) return true;
  const thumbnails = thumbnailsInView(stores);
  if (thumbnails.start === -1) return true;

  const thumbnailsLoaded = [];
  for (let i = thumbnails.start; i <= thumbnails.end; i++) {
    thumbnailsLoaded.push(!!getThumbnailBitmap(stores, i));
  }
  return thumbnailsLoaded.every(Boolean);
};

/**
 * Set show page loader to false when all pages and thumbnails in view are loaded.
 */
export const setShouldShowPageLoader = () => {
  if (getViewMode(stores) !== docViewModes.CLASSIC_VIEW) {
    stores.documentStore.setShouldShowPageLoader(false);
    return;
  }

  const shouldShowLoader = !(arePagesInViewLoaded() && areThumbnailsInViewLoaded());
  stores.documentStore.setShouldShowPageLoader(shouldShowLoader);
};
